<template>
  <div
    class="executive-summary-disabled-state page-break-avoid"
    data-testid="executive-summary-disabled-state"
  >
    <div class="executive-summary-disabled-state__placeholder" aria-hidden="true">
      <div
        v-for="row in PLACEHOLDER_ROWS"
        :key="row.id"
        class="executive-summary-disabled-state__row"
        :style="{ width: `${row.width}%` }"
        data-testid="executive-summary-disabled-state-row"
      ></div>
    </div>
    <div class="executive-summary-disabled-state__overlay">
      <div class="executive-summary-disabled-state__card pa-4">
        <div class="d-flex align-center ga-2 mb-3">
          <v-icon size="40" :icon="moduleIcon" />
          <span class="subtitle1" data-testid="executive-summary-disabled-state-title">
            {{ $t(`reports.executiveSummaryReport.modules.${module}.title`) }}
          </span>
        </div>
        <div
          class="body2"
          data-testid="executive-summary-disabled-state-message"
          v-html="message"
        ></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import { REPORT_MODULE_ICONS_S, type ReportModule } from "@/constants/reports";

const PLACEHOLDER_ROWS = [
  { id: 1, width: 22 },
  { id: 2, width: 96 },
  { id: 3, width: 64 },
  { id: 4, width: 88 },
  { id: 5, width: 40 },
  { id: 6, width: 72 },
];

export default defineComponent({
  props: {
    module: {
      type: String as PropType<ReportModule>,
      required: true,
    },
  },
  setup(props) {
    const i18n = useI18n();

    const moduleIcon = computed(() => REPORT_MODULE_ICONS_S[props.module]);

    const message = computed(() =>
      i18n.t("reports.executiveSummaryReport.notIncludedInSubscription", {
        module: i18n.t(`reports.executiveSummaryReport.modules.${props.module}.title`),
      })
    );

    return { moduleIcon, message, PLACEHOLDER_ROWS };
  },
});
</script>

<style scoped lang="scss">
// Both layers occupy the same single grid cell. Deliberately not `position: absolute`:
// html2canvas composites absolute overlays unpredictably, and this block has to survive
// the PDF export.
.executive-summary-disabled-state {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);

  &__placeholder,
  &__overlay {
    grid-area: 1 / 1;
  }

  &__placeholder {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 24px 0;
  }

  // Measured off the refreshed mock: a flat fill, not the gradient this used to fade out with.
  // `purple-faint` is our theme's #F5ECF9, the same value the mock's `App Brand/Purple Pale`
  // carries — the two design systems disagree on the name, not the colour.
  &__row {
    height: 16px;
    border-radius: 4px;
    background: rgb(var(--v-theme-purple-faint));
  }

  &__overlay {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  // The refreshed mock draws this as a plain bordered card: `Primary/Indigo Pale` at 1px, 8px
  // radius, white, and NO drop shadow — the panel lists no effects at all, where this used to
  // lift the card off the placeholder with one.
  &__card {
    max-width: 514px;
    min-width: 240px;
    background: rgb(var(--v-theme-surface));
    border-radius: 8px;
    border: 1px solid rgb(var(--v-theme-indigo-pale));
  }
}
</style>
